import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/useToast';
import { getAllFactories } from '../../services/firestore/factoryService';
import { getAllDoorGroups } from '../../services/firestore/doorGroupService';
import { getDocks } from '../../services/business/dockService';
import { getDailyReservations, updateReservation } from '../../services/business/reservationService';
import { VEHICLE_STATUS } from '../../constants/statuses';
import { useDataScope } from '../../hooks/useDataScope';
import { PERMISSIONS } from '../../constants/permissions';

// Components
import DockFilter from '../../components/dock/DockFilter';
import ReservationList from '../../components/dock/ReservationList';
import OperationDetail from '../../components/dock/OperationDetail';
import '../../styles/components/dockManagement.css';

const DockManagement = ({ userData }) => {
    const { user, can } = usePermissions(userData);
    const scope = useDataScope(userData);
    const { success, error: toastError } = useToast();
    const [searchParams] = useSearchParams();

    // Data States
    const [factories, setFactories] = useState([]);
    const [doorGroups, setDoorGroups] = useState([]);
    const [docks, setDocks] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filter Persistence
    // Priority: URL Param > LocalStorage > Default 'all'
    const getInitialFilter = (key) => {
        return localStorage.getItem(`dock_mgr_${key}`) || 'all';
    };

    const [filters, setFilters] = useState({
        factory: getInitialFilter('factory'),
        doorGroup: getInitialFilter('doorGroup'),
        dock: getInitialFilter('dock'),
    });

    // UI States
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [showCompleted, setShowCompleted] = useState(false);
    const [selectedReservation, setSelectedReservation] = useState(null);

    // Initial Load
    useEffect(() => {
        if (user?.tenantId) {
            loadInitialData();
        }
    }, [user?.tenantId]);

    // Handle URL Params override (e.g. coming from DockStatus)
    useEffect(() => {
        const dockIdParam = searchParams.get('dockId');
        if (dockIdParam && docks.length > 0) {
            // Find the dock to set factory/group automatically
            const targetDock = docks.find(d => d.id === dockIdParam);
            if (targetDock) {
                const targetGroup = doorGroups.find(g => g.id === targetDock.doorGroupId);
                const newFilters = {
                    dock: targetDock.id,
                    doorGroup: targetGroup ? targetGroup.id : 'all',
                    factory: targetGroup ? targetGroup.factoryId : 'all'
                };
                setFilters(newFilters);
                // Also save to local storage
                Object.keys(newFilters).forEach(k => {
                    localStorage.setItem(`dock_mgr_${k}`, newFilters[k]);
                });
            }
        }
    }, [searchParams, docks, doorGroups]);

    const loadInitialData = async () => {
        setLoading(true);
        try {
            const [fData, gData, dData] = await Promise.all([
                getAllFactories(user.tenantId),
                getAllDoorGroups(user.tenantId),
                getDocks(user.tenantId)
            ]);

            // --- DATA SCOPE FILTERING ---
            let filteredFactories = fData;
            let filteredGroups = gData;
            let filteredDocks = dData;

            if (!scope.isFullAccess) {
                if (scope.factoryIds.length > 0) {
                    filteredFactories = fData.filter(f => scope.factoryIds.includes(f.id));
                }
                if (scope.doorGroupIds.length > 0) {
                    filteredGroups = gData.filter(g => scope.doorGroupIds.includes(g.id));
                }
                filteredDocks = dData.filter(d => {
                    if (scope.dockIds.length > 0 && !scope.dockIds.includes(d.id)) return false;
                    if (scope.doorGroupIds.length > 0 && !scope.doorGroupIds.includes(d.doorGroupId)) return false;
                    if (scope.factoryIds.length > 0 && !scope.factoryIds.includes(d.factoryId)) return false;
                    return true;
                });
            }

            setFactories(filteredFactories);
            setDoorGroups(filteredGroups);
            setDocks(filteredDocks);

            // Load reservations initially
            await refreshReservations();

        } catch (err) {
            toastError("Veri yüklenirken hata oluştu.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const refreshReservations = async () => {
        if (!user?.tenantId) return;
        try {
            const todayStr = new Date().toISOString().split('T')[0];
            const data = await getDailyReservations(user.tenantId, todayStr);

            // Scope Filter Reservations
            let filteredRes = data;
            if (!scope.isFullAccess && scope.factoryIds.length > 0) {
                filteredRes = data.filter(r => scope.factoryIds.includes(r.factoryId));
            }

            setReservations(filteredRes);
        } catch (err) {
            console.error("Failed to fetch reservations", err);
        }
    };

    // Filter Logic
    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };

        // Reset sub-filters if parent changes
        if (key === 'factory') {
            newFilters.doorGroup = 'all';
            newFilters.dock = 'all';
        } else if (key === 'doorGroup') {
            newFilters.dock = 'all';
        }

        setFilters(newFilters);
        localStorage.setItem(`dock_mgr_${key}`, value);
        // Save others if reset
        if (key === 'factory') {
            localStorage.setItem(`dock_mgr_doorGroup`, 'all');
            localStorage.setItem(`dock_mgr_dock`, 'all');
        }
        if (key === 'doorGroup') {
            localStorage.setItem(`dock_mgr_dock`, 'all');
        }
    };

    // Filtered Reservations
    const filteredReservations = reservations.filter(res => {
        // 0. Status Visibility Filter - Show only vehicles that are in facility or completed
        const visibleStatuses = [
            VEHICLE_STATUS.PENDING_APPROVAL,
            VEHICLE_STATUS.APPROVED,
            VEHICLE_STATUS.FACILITY_ENTERED,
            VEHICLE_STATUS.IN_PARKING,
            VEHICLE_STATUS.LOADING_STARTED,
            VEHICLE_STATUS.LOADING_COMPLETED,
            VEHICLE_STATUS.FACILITY_EXITED,
            // Backward compatibility
            'pending', 'approved', 'entered', 'parking', 'loading', 'completed', 'exited', 'loading_start', 'loading_end'
        ];

        if (!visibleStatuses.includes(res.status)) return false;

        // 1. Filter by Dock ID if selected
        if (filters.dock !== 'all' && res.dockId !== filters.dock) return false;

        // 2. Filter by Group if selected (and no specific dock selected)
        if (filters.dock === 'all' && filters.doorGroup !== 'all') {
            // Check if dock relates to this group
            // We need to look up the dock of this reservation
            const resDock = docks.find(d => d.id === res.dockId);
            if (!resDock || resDock.doorGroupId !== filters.doorGroup) return false;
        }

        // 3. Filter by Factory if selected
        if (filters.dock === 'all' && filters.doorGroup === 'all' && filters.factory !== 'all') {
            const resDock = docks.find(d => d.id === res.dockId);
            if (!resDock) return false;
            const resGroup = doorGroups.find(g => g.id === resDock.doorGroupId);
            if (!resGroup || resGroup.factoryId !== filters.factory) return false;
        }

        return true;
    });

    const handleStatusUpdate = async (reservationId, newStatus) => {
        try {
            const res = reservations.find(r => r.id === reservationId);
            if (!res) return;

            const updatePayload = { status: newStatus };

            // Check if dock is already occupied by another vehicle
            if (newStatus === 'loading' || newStatus === 'arrived') {
                const activeOtherRes = reservations.find(r =>
                    r.dockId === res.dockId &&
                    r.id !== res.id &&
                    (r.status === 'loading' || r.status === 'arrived')
                );

                if (activeOtherRes) {
                    toastError(`Bu rampada şu an işlem gören başka bir araç var! (Belge No: ${activeOtherRes.planningDocumentNo || activeOtherRes.plate || '-'})`);
                    return;
                }
            }
            let newActualStartTime = res.actualStartTime;

            // Handle Start / Resume Logic
            if (newStatus === 'loading') {
                if (!res.actualStartTime) {
                    // First Start
                    updatePayload.actualStartTime = new Date(); // Passing Date object, service handles conversion
                    newActualStartTime = updatePayload.actualStartTime.toISOString();
                } else if (res.status === 'completed') {
                    // Resuming: Adjust start time to ignore the "paused" duration
                    // durationPaused = Now - LastUpdatedAt
                    // NewStart = OldStart + durationPaused

                    // Safely parse Firestore Timestamp or String
                    const getLastUpdate = (dateVal) => {
                        if (!dateVal) return new Date();
                        if (dateVal.toDate) return dateVal.toDate(); // Firestore Timestamp
                        return new Date(dateVal); // String or Date
                    };

                    if (res.actualStartTime) {
                        const oldStart = new Date(res.actualStartTime);

                        // Check if oldStart is valid
                        if (!isNaN(oldStart.getTime())) {
                            const completedAt = getLastUpdate(res.updatedAt);
                            const now = new Date();
                            const pauseDuration = now.getTime() - completedAt.getTime();

                            if (pauseDuration > 0) {
                                const adjustedStart = new Date(oldStart.getTime() + pauseDuration);
                                updatePayload.actualStartTime = adjustedStart;
                                newActualStartTime = adjustedStart.toISOString();
                            }
                        } else {
                            // Fallback if old data is corrupted
                            updatePayload.actualStartTime = new Date();
                            newActualStartTime = updatePayload.actualStartTime.toISOString();
                        }
                    } else {
                        // If no start time existed previously, treat as fresh start
                        updatePayload.actualStartTime = new Date();
                        newActualStartTime = updatePayload.actualStartTime.toISOString();
                    }
                }
            }

            await updateReservation(reservationId, updatePayload, user.email, user.tenantId);
            success(newStatus === 'loading' ? 'Yükleme başlatıldı' : 'Yükleme tamamlandı');

            // Refresh logic - optimistic update
            const updateState = (prev) => prev.map(r =>
                r.id === reservationId ? {
                    ...r,
                    status: newStatus,
                    actualStartTime: newActualStartTime,
                    updatedAt: new Date() // Optimistic update
                } : r
            );

            setReservations(updateState);
            setSelectedReservation(current => current ? {
                ...current,
                status: newStatus,
                actualStartTime: newActualStartTime,
                updatedAt: new Date()
            } : null);

            if (newStatus === 'completed') {
                // Keep the detail screen open even if completed, as requested for "Restart" capability
                // But we need to make sure state reflects 'completed' to show correct button
            }

        } catch (err) {
            console.error(err);
            toastError("Durum güncellenemedi");
        }
    };

    if (loading) return <div className="p-10 text-center">Yükleniyor...</div>;

    return (
        <div className="dock-management-container">
            <div className="dock-header">
                <h1 className="dock-title">Rampa Yönetimi</h1>
            </div>

            {!selectedReservation ? (
                /* Screen 1: List View */
                <>
                    <DockFilter
                        factories={factories}
                        doorGroups={doorGroups}
                        docks={docks}
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        isOpen={isFilterOpen}
                        toggleOpen={() => setIsFilterOpen(!isFilterOpen)}
                    />

                    <ReservationList
                        reservations={filteredReservations}
                        showCompleted={showCompleted}
                        setShowCompleted={setShowCompleted}
                        onSelectReservation={setSelectedReservation}
                    />
                </>
            ) : (
                /* Screen 2: Operation Detail */
                <OperationDetail
                    reservation={selectedReservation}
                    dockName={docks.find(d => d.id === selectedReservation.dockId)?.name}
                    onBack={() => setSelectedReservation(null)}
                    onStatusUpdate={handleStatusUpdate}
                    canExecute={can(PERMISSIONS.DOCK_PROCESS, PERMISSIONS.EXECUTE)}
                />
            )}
        </div>
    );
};

export default DockManagement;
